import { Aggregation } from './chart-config.model';
import {
	formulaMetricCubeName,
	isPerConnectionSchemaCube,
} from '../utils/analytics-service-icon-assets';
import {
	humanizeTableColumnHeader,
	isHumanReadableHeaderLabel,
} from '../utils/table-column-header.util';

export interface AnalyticsSchemaField {
	name: string;
	label: string;
	kind: 'metric' | 'dimension' | string;
	description?: string;
	dataSources?: string[];
	sourceGroup?: string;
	/** Display key for data-source icon (e.g. `bigquery`, `facebookAds`). */
	dataSource?: string | null;
	aggregation?: Aggregation;
}

export interface AnalyticsSchemaSource {
	type: string;
	label: string;
}

export interface AnalyticsSchemaResult {
	metrics: AnalyticsSchemaField[];
	dimensions: AnalyticsSchemaField[];
	sources: AnalyticsSchemaSource[];
}

/**
 * Human-readable title for schema fields in UI (DATA tab pickers, etc.).
 * Prefers API `label` (humanized when technical camelCase). Falls back to
 * humanizing `name` only when label is missing — never prefer a `ds_*` cube
 * path over a member label (the service icon already identifies the source).
 */
export function schemaFieldDisplayLabel(field: {
	name: string;
	label?: string | null;
}): string {
	const name = field.name?.trim() ?? '';
	const label = field.label?.trim() ?? '';
	if (label) {
		if (isHumanReadableHeaderLabel(label)) {
			return label;
		}
		return humanizeTableColumnHeader(label);
	}
	return humanizeTableColumnHeader(name);
}

/** Connector / connection key for a schema field (`sourceGroup` or first `dataSources` entry). */
export function schemaSourceKeyForField(field: {
	name: string;
	sourceGroup?: string;
	dataSources?: string[];
}): string | undefined {
	const fromGroup = field.sourceGroup?.trim();
	if (fromGroup) {
		return fromGroup;
	}
	const fromSources = field.dataSources?.[0]?.trim();
	if (fromSources) {
		return fromSources;
	}
	const cube = formulaMetricCubeName(field.name);
	return cube || undefined;
}

export function schemaSourceLabelForField(
	field: {
		name: string;
		sourceGroup?: string;
		dataSources?: string[];
	},
	sources: readonly AnalyticsSchemaSource[]
): string | undefined {
	const key = schemaSourceKeyForField(field);
	if (!key) {
		return undefined;
	}
	return sources.find(source => source.type === key)?.label?.trim();
}

/**
 * Picker row title for Reports card settings and schema field selects.
 * Appends the connection / client label for `ds_*` cubes and colliding base labels.
 */
export function schemaFieldPickerDisplayLabel(
	field: { name: string; label?: string | null },
	sourceLabel?: string | null,
	duplicateBaseLabelCount = 1
): string {
	const base = schemaFieldDisplayLabel(field);
	const connection = sourceLabel?.trim();
	if (!connection) {
		return base;
	}
	if (
		isPerConnectionSchemaCube(formulaMetricCubeName(field.name)) ||
		duplicateBaseLabelCount > 1
	) {
		return `${base} (${connection})`;
	}
	return base;
}

export function schemaFieldPickerLabelForField(
	field: {
		name: string;
		label?: string | null;
		sourceGroup?: string;
		dataSources?: string[];
	},
	options: readonly {
		name: string;
		label?: string | null;
		sourceGroup?: string;
		dataSources?: string[];
	}[],
	sources: readonly AnalyticsSchemaSource[]
): string {
	const base = schemaFieldDisplayLabel(field);
	let duplicateBaseLabelCount = 0;
	for (const option of options) {
		if (schemaFieldDisplayLabel(option) === base) {
			duplicateBaseLabelCount++;
		}
	}
	return schemaFieldPickerDisplayLabel(
		field,
		schemaSourceLabelForField(field, sources),
		duplicateBaseLabelCount
	);
}

export function buildSchemaFieldPickerLabelMap(
	fields: readonly AnalyticsSchemaField[],
	sources: readonly AnalyticsSchemaSource[]
): ReadonlyMap<string, string> {
	const map = new Map<string, string>();
	for (const field of fields) {
		const pickerLabel = schemaFieldPickerLabelForField(
			field,
			fields,
			sources
		);
		if (pickerLabel !== schemaFieldDisplayLabel(field)) {
			map.set(field.name, pickerLabel);
		}
	}
	return map;
}

/** UI label for schema `kind` (`dimension` / `metric`). */
export function schemaFieldKindLabel(
	kind: string | null | undefined
): string | null {
	const normalized = kind?.trim().toLowerCase();
	if (normalized === 'dimension') {
		return 'Dimension';
	}
	if (normalized === 'metric') {
		return 'Metric';
	}
	return null;
}
